import { Boxes, Trash2, RotateCw } from 'lucide-react';
import { usePlanEditor, FIXTURE_KINDS, fixtureKindFor } from './PlanEditorContext';
import { FoldableSection } from './FoldableSection';

/** V15.08 — Barre d'outils : choix du type d'élément fixe → mode `place:fixture:<kind>`. */
export const FixtureToolPicker = ({ mode, setMode, disabled }) => {
  const active = mode.startsWith('place:fixture:');
  const current = active ? mode.slice('place:fixture:'.length) : '';
  return (
    <div className="flex items-center gap-1" data-testid="tool-fixture-picker" title="Poser un élément fixe (îlot, cheminée, colonne, placard, douche…) — cliquez sur le canvas pour le placer, redimensionnez via la poignée">
      <Boxes className={`w-3.5 h-3.5 ${active ? 'text-amber-300' : 'text-gray-400'}`} />
      <select
        value={current}
        disabled={disabled}
        onChange={(e) => setMode(e.target.value ? `place:fixture:${e.target.value}` : 'select')}
        className={`h-7 text-[11px] rounded px-1.5 border focus:outline-none ${active ? 'bg-amber-500/15 border-amber-400/50 text-amber-100' : 'bg-white/[0.04] border-white/10 text-gray-300'}`}
        data-testid="tool-fixture-select"
      >
        <option value="">Élément fixe…</option>
        {FIXTURE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
      </select>
    </div>
  );
};

/** Panneau droit : liste des éléments fixes + hauteur + suppression. */
export const FixturesPanelSection = ({ disabled }) => {
  const { fixtures, selectedFixtureId, setSelectedFixtureId, actions } = usePlanEditor();
  if (!fixtures || fixtures.length === 0) return null;
  return (
    <FoldableSection title={`Éléments fixes · ${fixtures.length}`} icon={Boxes} iconClass="text-amber-300" testid="fixtures-panel-section">
      <div className="mt-1.5 space-y-1">
        {fixtures.map((fx) => {
          const k = fixtureKindFor(fx.kind);
          const isSel = fx.id === selectedFixtureId;
          const full = !fx.height_m || fx.height_m <= 0;
          return (
            <div key={fx.id}
                 className={`rounded border px-2 py-1.5 text-[11px] cursor-pointer transition-colors ${isSel ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/10 hover:bg-white/[0.04]'}`}
                 onClick={() => setSelectedFixtureId(isSel ? null : fx.id)}
                 data-testid={`fixture-row-${fx.id}`}>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: k.color }} />
                <span className="font-medium" style={{ color: k.color }}>{k.label}</span>
                <span className="text-gray-400 hud-tabular">{full ? 'jusqu\'au plafond' : `${fx.height_m.toFixed(2)} m`}</span>
                <button type="button" className="ml-auto text-rose-300/70 hover:text-rose-300"
                        onClick={(e) => { e.stopPropagation(); actions.deleteFixture(fx.id); }}
                        disabled={disabled} title="Supprimer" data-testid={`fixture-delete-${fx.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {isSel && (
                <div className="mt-1.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <select value={fx.kind} disabled={disabled}
                          onChange={(e) => { const nk = fixtureKindFor(e.target.value); actions.updateFixture(fx.id, { kind: nk.value, height_m: nk.height_m }); }}
                          className="h-6 text-[10.5px] rounded px-1 bg-white/[0.06] border border-white/10 text-white"
                          data-testid={`fixture-kind-${fx.id}`}>
                    {FIXTURE_KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <label className="text-[10.5px] text-gray-300 ml-auto">Hauteur</label>
                  <input type="number" min={0} max={4} step={0.05} disabled={disabled}
                         value={full ? 0 : fx.height_m}
                         onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v >= 0) actions.updateFixture(fx.id, { height_m: +v.toFixed(2) }); }}
                         className="w-16 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right focus:outline-none focus:border-amber-400/60"
                         title="0 = jusqu'au plafond (HSP)"
                         data-testid={`fixture-height-${fx.id}`} />
                  <span className="text-[10px] text-gray-400">m</span>
                </div>
              )}
              {isSel && (
                <div className="mt-1.5 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()} data-testid={`fixture-rotation-row-${fx.id}`}>
                  <RotateCw className="w-3 h-3 text-gray-400" />
                  <label className="text-[10.5px] text-gray-300">Rotation</label>
                  <button type="button" disabled={disabled}
                          onClick={() => actions.updateFixture(fx.id, { rotation_deg: ((+fx.rotation_deg || 0) + 90) % 360 })}
                          className="h-6 px-1.5 rounded text-[10px] border border-white/10 text-gray-300 hover:bg-white/5"
                          title="Pivoter de 90°" data-testid={`fixture-rotate90-${fx.id}`}>+90°</button>
                  <input type="range" min={0} max={359} step={1} disabled={disabled}
                         value={Math.round(+fx.rotation_deg || 0)}
                         onChange={(e) => actions.updateFixture(fx.id, { rotation_deg: parseInt(e.target.value, 10) })}
                         className="flex-1 accent-amber-400" data-testid={`fixture-rotation-slider-${fx.id}`} />
                  <input type="number" min={0} max={359} step={1} disabled={disabled}
                         value={Math.round(+fx.rotation_deg || 0)}
                         onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) actions.updateFixture(fx.id, { rotation_deg: ((v % 360) + 360) % 360 }); }}
                         className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right focus:outline-none focus:border-amber-400/60"
                         data-testid={`fixture-rotation-${fx.id}`} />
                  <span className="text-[10px] text-gray-400">°</span>
                </div>
              )}
            </div>
          );
        })}
        <p className="text-[9.5px] text-gray-500">Extrudés dans la maquette blanche (bloc plein) — l'IA les texture à leur place exacte. Hauteur 0 = plafond.</p>
      </div>
    </FoldableSection>
  );
};
